'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { Loader2, Send, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { FeedComment } from '@kit/community';
import {
  createCommentAction,
  deleteCommentAction,
  getPostComments,
} from '@kit/community/server/actions';
import { dancerPath, organizationPath } from '@kit/shared/handles';
import { Avatar, AvatarFallback } from '@kit/ui/avatar';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

interface CommentsSectionProps {
  postId: string;
  initialComments: FeedComment[];
  currentProfileId?: string | null;
  onCommentAdded?: () => void;
  onCommentDeleted?: () => void;
}

function formatTimeAgo(
  dateString: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return t('community:time.justNow');
  if (diffInSeconds < 3600)
    return t('community:time.minutesShort', {
      count: Math.floor(diffInSeconds / 60),
    });
  if (diffInSeconds < 86400)
    return t('community:time.hoursShort', {
      count: Math.floor(diffInSeconds / 3600),
    });
  if (diffInSeconds < 604800)
    return t('community:time.daysShort', {
      count: Math.floor(diffInSeconds / 86400),
    });
  return date.toLocaleDateString();
}

function CommentItem({
  comment,
  currentProfileId,
  onReply,
  onDelete,
  depth = 0,
}: {
  comment: FeedComment;
  currentProfileId?: string | null;
  onReply: (commentId: string) => void;
  onDelete: (commentId: string) => Promise<void>; // i18n-ignore TypeScript generic, not user-facing
  depth?: number;
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();
  const author = comment.author;

  if (!author) return null;

  const profileLink =
    author.type === 'dancer'
      ? dancerPath(author.username)
      : organizationPath(author.username);

  const isOwner = currentProfileId === author.id;
  const maxDepth = 2;

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(comment.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={depth > 0 ? 'border-border ml-8 border-l pl-4' : ''}>
      <div className="flex gap-2 py-2">
        <Link href={profileLink}>
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs">
              {author.username?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </Link>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Link
              href={profileLink}
              className="text-sm font-medium hover:underline"
            >
              {author.full_name || author.username}
            </Link>
            <span className="text-muted-foreground text-xs">
              {formatTimeAgo(comment.created_at, t)}
            </span>
          </div>

          <p className="text-foreground mt-1 text-sm whitespace-pre-wrap">
            {comment.body}
          </p>

          <div className="mt-1 flex items-center gap-2">
            {depth < maxDepth && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-auto p-0 text-xs"
                onClick={() => onReply(comment.id)}
              >
                <Trans i18nKey="community:comments.reply" defaults="Reply" />
              </Button>
            )}
            {isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive h-auto p-0 text-xs"
                onClick={handleDelete}
                disabled={isDeleting}
                aria-label={t('community:comments.deleteComment')}
              >
                {isDeleting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              currentProfileId={currentProfileId}
              onReply={onReply}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentsSection({
  postId,
  initialComments,
  currentProfileId,
  onCommentAdded,
  onCommentDeleted,
}: CommentsSectionProps) {
  const [comments, setComments] = useState<FeedComment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!newComment.trim() || isPending) return;

    const body = newComment;
    setNewComment('');

    startTransition(async () => {
      const result = await createCommentAction(
        postId,
        body,
        replyingTo || undefined,
      );

      if (result.success && result.commentId) {
        toast.success(t('community:comments.posted'));
        setReplyingTo(null);
        onCommentAdded?.();
        // Re-fetch comments to show the new one in the list
        const refreshed = await getPostComments(postId);
        setComments(refreshed.comments);
      } else {
        toast.error(result.error || t('community:comments.postFailed'));
        setNewComment(body); // Restore the comment text
      }
    });
  };

  const handleDelete = async (commentId: string) => {
    const result = await deleteCommentAction(commentId);

    if (result.success) {
      // Remove comment from state
      setComments((prev) => {
        const removeComment = (comments: FeedComment[]): FeedComment[] => {
          return comments
            .filter((c) => c.id !== commentId)
            .map((c) => ({
              ...c,
              replies: c.replies ? removeComment(c.replies) : [],
            }));
        };
        return removeComment(prev);
      });
      onCommentDeleted?.();
      toast.success(t('community:comments.deleted'));
    } else {
      toast.error(result.error || t('community:comments.deleteFailed'));
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
    // Find the comment being replied to for UX
    const findComment = (comments: FeedComment[]): FeedComment | undefined => {
      for (const c of comments) {
        if (c.id === commentId) return c;
        if (c.replies) {
          const found = findComment(c.replies);
          if (found) return found;
        }
      }
      return undefined;
    };
    const repliedComment = findComment(comments);
    if (repliedComment?.author) {
      setNewComment(`@${repliedComment.author.username} `);
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setNewComment('');
  };

  return (
    <div className="border-border border-t pt-3">
      {/* Comment input */}
      <div className="flex gap-2">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">U</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          {replyingTo && (
            <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs">
              <span>
                <Trans
                  i18nKey="community:comments.replyingTo"
                  defaults="Replying to comment"
                />
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={cancelReply}
              >
                <Trans i18nKey="common:cancel" defaults="Cancel" />
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            <Textarea
              id="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  void handleSubmit();
                }
              }}
              placeholder={t('community:comments.writePlaceholder')}
              className="min-h-[60px] resize-none"
              disabled={isPending}
              aria-label={t('community:comments.writeAriaLabel')}
            />
            <Button
              size="icon"
              onClick={handleSubmit}
              disabled={!newComment.trim() || isPending}
              aria-label={t('community:comments.postAriaLabel')}
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Comments list */}
      {comments.length > 0 ? (
        <div className="mt-4 space-y-1">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentProfileId={currentProfileId}
              onReply={handleReply}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground mt-4 text-center text-sm">
          <Trans
            i18nKey="community:comments.empty"
            defaults="No comments yet. Be the first to comment!"
          />
        </p>
      )}
    </div>
  );
}
